import { useState, useRef, useEffect, useLayoutEffect, useContext } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Bell,
  Search,
  Menu,
  Building2,
  X,
  Wallet,
  ClipboardList,
  FileText,
  AlertTriangle,
} from "lucide-react";
import { AuthContext } from "../../Moduels/Context/AuthContext";
import { fetchActionableLeaveCount } from "../../Moduels/leave/leaveApi";
import { loadOrganisationMe } from "../../services/appQueries";
import { queryClient } from "../../lib/queryClient";
import { orgScope } from "../../services/orgScope";
import {
  LEAVE_APPROVAL_NAV_STATE,
  LEAVE_QUEUE_UPDATED_EVENT,
} from "../../Moduels/leave/leaveQueueEvents";
import UserDropdown from "../user/UserDropdown";
import OrganisationLogo from "../common/OrganisationLogo";
import {
  buildNavbarNotifications,
  loadReadNotificationIds,
  saveReadNotificationIds,
} from "../../config/navbarNotifications";
import { fetchCachedDashboardOverview, invalidateDashboardOverview } from "../../services/dashboardApi";
import { hasRole, isPayrollDashboardRole, TEAM_LEADS } from "../../rbac/access";
import { ORG_BRANDING_UPDATED_EVENT } from "../../utils/orgBranding";

export default function Navbar({ toggle }) {
  const nav = useNavigate();
  const { user, role, principalType, logout: authLogout } = useContext(AuthContext);

  const [openNotify, setOpenNotify] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [org, setOrg] = useState(null);
  const [orgLogoVersion, setOrgLogoVersion] = useState(() => Date.now());
  const [scrolled, setScrolled] = useState(false);
  const [readIds, setReadIds] = useState(() => loadReadNotificationIds());
  const [overview, setOverview] = useState(null);
  const [pendingLeaveCount, setPendingLeaveCount] = useState(0);

  const notifyRef = useRef();
  const notifyTriggerRef = useRef();
  const notifyPanelRef = useRef();
  const [notifyMenuStyle, setNotifyMenuStyle] = useState({ top: 0, left: 0, width: 360 });

  const logout = () => {
    authLogout();
    nav("/login");
  };

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 4);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    const fn = (e) => {
      const target = e.target;
      if (
        notifyRef.current?.contains(target) ||
        notifyPanelRef.current?.contains(target)
      ) {
        return;
      }
      setOpenNotify(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpenNotify(false);
    };
    document.addEventListener("mousedown", fn);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", fn);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const updateNotifyPosition = () => {
    const trigger = notifyTriggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const width = Math.min(360, window.innerWidth - 16);
    const left = Math.min(Math.max(8, rect.right - width), window.innerWidth - width - 8);
    const top = rect.bottom + 8;
    setNotifyMenuStyle({ top, left, width });
  };

  useLayoutEffect(() => {
    if (!openNotify) return undefined;
    updateNotifyPosition();
    const onReflow = () => updateNotifyPosition();
    window.addEventListener("resize", onReflow, { passive: true });
    window.addEventListener("scroll", onReflow, true);
    return () => {
      window.removeEventListener("resize", onReflow);
      window.removeEventListener("scroll", onReflow, true);
    };
  }, [openNotify]);

  const fetchOrg = async () => {
    try {
      const data = await loadOrganisationMe();
      setOrg(data);
      setOrgLogoVersion(Date.now());
    } catch (err) {
      console.error("Organisation load error:", err?.response?.data || err.message);
      setOrg(null);
    }
  };

  useEffect(() => {
    if (!role && !principalType) return undefined;
    fetchOrg();
    return undefined;
  }, [role, principalType]);

  useEffect(() => {
    const onBranding = () => {
      queryClient.invalidateQueries({ queryKey: ["organisation-me", orgScope()] });
      fetchOrg();
    };
    window.addEventListener(ORG_BRANDING_UPDATED_EVENT, onBranding);
    return () => window.removeEventListener(ORG_BRANDING_UPDATED_EVENT, onBranding);
  }, []);

  const orgId =
    user?.organisation?.id ||
    user?.organisation?.organisation_id ||
    user?.user?.organisation_id ||
    user?.organisation_id ||
    "";

  useEffect(() => {
    let cancelled = false;

    async function loadNotificationData() {
      if (isPayrollDashboardRole(role)) {
        try {
          const data = await fetchCachedDashboardOverview(role);
          if (!cancelled) setOverview(data);
        } catch {
          if (!cancelled) setOverview(null);
        }
      } else if (!cancelled) {
        setOverview(null);
      }

      if (hasRole(role, TEAM_LEADS) && orgId) {
        try {
          const count = await fetchActionableLeaveCount(orgId, { fresh: false });
          if (!cancelled) setPendingLeaveCount(count);
        } catch {
          if (!cancelled) setPendingLeaveCount(0);
        }
      } else if (!cancelled) {
        setPendingLeaveCount(0);
      }
    }

    loadNotificationData();

    const onLeaveQueueUpdated = () => {
      if (hasRole(role, TEAM_LEADS) && orgId) {
        fetchActionableLeaveCount(orgId, { fresh: true })
          .then((count) => {
            if (!cancelled) setPendingLeaveCount(count);
          })
          .catch(() => {
            if (!cancelled) setPendingLeaveCount(0);
          });
      }
      if (isPayrollDashboardRole(role)) {
        invalidateDashboardOverview(queryClient);
        fetchCachedDashboardOverview(role)
          .then((data) => {
            if (!cancelled) setOverview(data);
          })
          .catch(() => {
            if (!cancelled) setOverview(null);
          });
      }
    };
    window.addEventListener(LEAVE_QUEUE_UPDATED_EVENT, onLeaveQueueUpdated);

    return () => {
      cancelled = true;
      window.removeEventListener(LEAVE_QUEUE_UPDATED_EVENT, onLeaveQueueUpdated);
    };
  }, [role, orgId]);

  const notifications = buildNavbarNotifications({
    role,
    overview,
    pendingLeaveCount,
    readIds,
  });
  const unreadCount = notifications.filter((n) => n.unread).length;

  const handleNotificationClick = (item) => {
    const next = new Set(readIds);
    next.add(item.id);
    setReadIds(next);
    saveReadNotificationIds(next);
    setOpenNotify(false);
    if (item.path) {
      const leaveNav =
        (item.path && (item.path.includes("tab=approval") || item.path === "/leave/approval")) ||
        String(item.id || "").startsWith("leave-pending");
      nav(item.path, leaveNav ? { state: LEAVE_APPROVAL_NAV_STATE } : undefined);
    }
  };
  const orgName = org?.name || user?.organisation?.name || "Your Organisation";

  const notificationPanel = (
    <AnimatePresence>
      {openNotify ? (
        <motion.div
          ref={notifyPanelRef}
          className="nb-dropdown nb-notif-panel"
          role="dialog"
          aria-label="Notifications"
          initial={{ opacity: 0, y: -6, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -6, scale: 0.98 }}
          transition={{ duration: 0.18 }}
          style={{
            position: "fixed",
            top: notifyMenuStyle.top,
            left: notifyMenuStyle.left,
            width: notifyMenuStyle.width,
            right: "auto",
            zIndex: "var(--z-dropdown, 1200)",
          }}
        >
          <div className="nb-notif-header">
            <span className="nb-notif-title">Notifications</span>
            {unreadCount > 0 ? <span className="nb-notif-badge">{unreadCount} new</span> : null}
          </div>
          {notifications.length === 0 ? (
            <div className="nb-notif-empty">No notifications right now.</div>
          ) : (
            notifications.map((n) => (
              <button
                key={n.id}
                type="button"
                className={`nb-notif-item${n.unread ? " unread" : ""}`}
                onClick={() => handleNotificationClick(n)}
              >
                <motion.div
                  className="nb-notif-icon-wrap"
                  style={{ background: n.bg, color: n.color }}
                  whileHover={{ scale: 1.04 }}
                >
                  <NotificationIcon type={n.type} />
                </motion.div>
                <div className="nb-notif-body">
                  <div className="nb-notif-item-title">{n.title}</div>
                  <div className="nb-notif-item-desc">{n.desc}</div>
                </div>
                <span className="nb-notif-time">{n.time}</span>
              </button>
            ))
          )}
          <div className="nb-notif-footer">
            <button
              type="button"
              className="nb-notif-view-all"
              onClick={() => {
                setOpenNotify(false);
                nav("/noticeboard");
              }}
            >
              View all notifications →
            </button>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );

  return (
    <header className={`nb${scrolled ? " scrolled" : ""}`}>
      <motion.div className="nb-inner">
        <div className="nb-left">
          <button className="nb-hamburger" onClick={toggle} aria-label="Toggle menu" type="button">
            <Menu size={18} />
          </button>
          <motion.div className="nb-org" layout={false} whileHover={{ scale: 1.01 }}>
            <div className="nb-org-icon">
              {org?.logo_url ? (
                <OrganisationLogo
                  logoUrl={org.logo_url}
                  cacheBust={orgLogoVersion}
                  className="nb-org-logo-img"
                  alt=""
                />
              ) : (
                <Building2 size={12} />
              )}
            </div>
            <span className="nb-org-name">{orgName}</span>
          </motion.div>
        </div>

        <div className="nb-search-wrap">
          <label className="nb-search" aria-label="Search payroll workspace">
            <Search size={14} className="nb-search-icon" aria-hidden />
            <input
              placeholder="Search employees, payroll, reports…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search"
            />
            {searchQuery ? (
              <button
                type="button"
                className="nb-search-clear"
                onClick={() => setSearchQuery("")}
                aria-label="Clear search"
              >
                <X size={12} />
              </button>
            ) : (
              <motion.div style={{ display: "flex", gap: 4, opacity: 0.7 }} aria-hidden initial={false}>
                <span className="nb-search-kbd">⌘</span>
                <span className="nb-search-kbd">K</span>
              </motion.div>
            )}
          </label>
        </div>

        <div className="nb-actions">
          <div ref={notifyRef} style={{ position: "relative" }}>
            <button
              ref={notifyTriggerRef}
              className="nb-btn"
              type="button"
              onClick={() => setOpenNotify((v) => !v)}
              title="Notifications"
              aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ""}`}
              aria-expanded={openNotify}
              aria-haspopup="dialog"
            >
              <Bell size={17} />
              {unreadCount > 0 ? <span className="nb-badge-count">{unreadCount}</span> : null}
            </button>

            {typeof document !== "undefined" ? createPortal(notificationPanel, document.body) : null}
          </div>

          <div className="nb-divider" aria-hidden />

          <UserDropdown
            me={user}
            orgName={orgName}
            orgLogoUrl={org?.logo_url}
            orgLogoVersion={orgLogoVersion}
            onLogout={() => {
              setOpenNotify(false);
              logout();
            }}
          />
        </div>
      </motion.div>
    </header>
  );
}

function NotificationIcon({ type }) {
  const map = {
    payroll: Wallet,
    leave: ClipboardList,
    tax: FileText,
    alert: AlertTriangle,
  };
  const Icon = map[type] || AlertTriangle;
  return <Icon size={16} aria-hidden />;
}
